import { normalizeMealNameForSave } from './mealPlanAlternatives';

export const ALLOWED_MEAL_SLOTS = [
  'Pre-Morning Snack',
  'Breakfast',
  'Morning Snack',
  'Lunch',
  'Afternoon Snack',
  'Pre-Workout Meal',
  'Post-Workout Meal',
  'Dinner',
  'Evening Snack',
  'Night Snack',
];

const isFiniteNumber = (value) => Number.isFinite(Number(value));

function validateIngredient(ingredient, path, errors, warnings) {
  if (!ingredient || typeof ingredient !== 'object') {
    errors.push(`${path}: ingredient must be an object`);
    return;
  }
  const item = (ingredient.item || ingredient.name || '').trim();
  if (!item) {
    errors.push(`${path}: ingredient name is required`);
  }
  const portion = ingredient['portionSI(gram)'];
  if (portion != null && portion !== '' && !isFiniteNumber(portion)) {
    warnings.push(`${path}: portionSI(gram) is not a valid number`);
  }
  if (portion != null && Number(portion) <= 0) {
    warnings.push(`${path}: portionSI(gram) is zero or missing`);
  }
  if (ingredient.per_100g) {
    warnings.push(`${path}: per_100g should be stripped before save (will be removed on persist)`);
  }
}

function validateDish(dish, path, errors, warnings, { requireIngredients = true } = {}) {
  if (!dish || typeof dish !== 'object') {
    errors.push(`${path}: dish is required`);
    return;
  }
  const title = (dish.meal_title || dish.meal_name || dish.title || '').trim();
  if (!title) {
    warnings.push(`${path}: meal_title is empty`);
  }
  const ingredients = dish.ingredients;
  if (!Array.isArray(ingredients)) {
    errors.push(`${path}: ingredients must be an array`);
    return;
  }
  if (requireIngredients && ingredients.length === 0) {
    errors.push(`${path}: at least one ingredient is required`);
  }
  ingredients.forEach((ing, idx) => validateIngredient(ing, `${path}.ingredients[${idx}]`, errors, warnings));
}

/**
 * Validate editor-state meal plan before save.
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export function validateMealPlanForSave(planData, translations) {
  const errors = [];
  const warnings = [];
  const englishTranslations = translations?.english || {};
  const hebrewTranslations = translations?.hebrew || {};

  if (!planData || typeof planData !== 'object') {
    return { valid: false, errors: ['Meal plan data is required'], warnings: [] };
  }

  if (!Array.isArray(planData.meals) || planData.meals.length === 0) {
    errors.push('At least one meal is required');
    return { valid: false, errors, warnings };
  }

  planData.meals.forEach((meal, mealIndex) => {
    const prefix = `meals[${mealIndex}]`;
    if (!meal || typeof meal !== 'object') {
      errors.push(`${prefix}: meal must be an object`);
      return;
    }

    const resolvedSlot = normalizeMealNameForSave(meal.meal, englishTranslations, hebrewTranslations);
    if (!resolvedSlot || !ALLOWED_MEAL_SLOTS.includes(resolvedSlot)) {
      errors.push(`${prefix}: unknown meal slot "${meal.meal || ''}"`);
    }

    if (!meal.main) {
      errors.push(`${prefix}: main dish is required`);
    } else {
      validateDish(meal.main, `${prefix}.main`, errors, warnings);
    }

    if (meal.alternative) {
      validateDish(meal.alternative, `${prefix}.alternative`, errors, warnings, { requireIngredients: false });
    }

    (meal.alternatives || []).forEach((alt, altIndex) => {
      validateDish(alt, `${prefix}.alternatives[${altIndex}]`, errors, warnings, { requireIngredients: false });
    });
  });

  return { valid: errors.length === 0, errors, warnings };
}
